import type { ComputedRef } from 'vue'
import type { PermissionAction, PermissionContainer, PermissionMap, ValidActions } from '~/types/permissions'
import { useAuthStore } from '~/stores/authStore'

export const CATEGORY_TO_ACTION: Record<string, PermissionAction> = {
  OP: 'op_action',
  DEBUG: 'debug_action',
  ADMIN: 'admin_action',
  CONF: 'conf_action'
}

interface UsePermissionsResult {
  can: <T extends PermissionContainer>(container: T, action: ValidActions<T>) => ComputedRef<boolean>
  cannot: <T extends PermissionContainer>(container: T, action: ValidActions<T>) => ComputedRef<boolean>
  canAny: (containers: string[], action: ValidActions<PermissionContainer>) => ComputedRef<boolean>
  canAnyContainer: (containers: string[]) => ComputedRef<boolean>
  canCategory: (container: string, category: string) => boolean
  isLoaded: ComputedRef<boolean>
  isReady: ComputedRef<boolean>
}

export const usePermissions = (): UsePermissionsResult => {
  const authStore = useAuthStore()

  const can = <T extends PermissionContainer>(container: T, action: ValidActions<T>): ComputedRef<boolean> => {
    return computed(() => {
      if (!authStore.permissionsLoaded) return false
      const permissions: PermissionMap = authStore.permissions || {}
      return permissions[container]?.includes(action) ?? false
    })
  }

  const cannot = <T extends PermissionContainer>(container: T, action: ValidActions<T>): ComputedRef<boolean> => {
    return computed(() => !can(container, action).value)
  }

  const canAny = (containers: string[], action: ValidActions<PermissionContainer>): ComputedRef<boolean> => {
    return computed(() => {
      if (!authStore.permissionsLoaded) return false
      const permissions: PermissionMap = authStore.permissions || {}
      return containers.some((c) => permissions[c]?.includes(action) ?? false)
    })
  }

  const canAnyContainer = (containers: string[]): ComputedRef<boolean> => {
    return computed(() => {
      if (!authStore.permissionsLoaded) return false
      const permissions: PermissionMap = authStore.permissions || {}
      return containers.some((c) => !(c in permissions) || (permissions[c]?.length ?? 0) > 0)
    })
  }

  const canCategory = (container: string, category: string): boolean => {
    const action = CATEGORY_TO_ACTION[category]
    if (!action) return false
    return can(container, action).value
  }

  const isLoaded = computed(() => authStore.permissionsLoaded)
  const isReady = computed(() => authStore.isAuthenticated && authStore.permissionsLoaded)

  return {
    can,
    cannot,
    canAny,
    canAnyContainer,
    canCategory,
    isLoaded,
    isReady
  }
}
